import { h } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import { useSignal } from '@preact/signals'
import { BubbleContent, isImageOnlyMessage } from './BubbleContent.jsx'
import { AttachmentView } from './AttachmentView.jsx'
import { BubbleReactions } from './BubbleReactions.jsx'
import { BubbleReadReceipts } from './BubbleReadReceipts.jsx'
import { chatApi } from '../chat-api.js'
import { t } from '../chat-i18n.js'
import { renderMarkdownPreview } from '../chat-markdown-editor.js'

const CONSECUTIVE_THRESHOLD_MS = 5 * 60 * 1000

/**
 * MessageBubble — a single message in the thread.
 *
 * Props: { message, prevMessage, store, cable, highlighted, allMessages }
 *
 * message shape:
 *   { id, body, body_html, attachments, creator_person, reply_to,
 *     created_at, reactions, _sendStatus, _clientId }
 */
export function MessageBubble({ message, prevMessage, store, cable, highlighted, allMessages }) {
  const personId  = store.personId
  const roomId    = store.activeRoomId.value

  const creator   = message.creator_person || {}
  const isOwn     = creator.id === personId || !!message._sendStatus
  const isFailed  = message._sendStatus === 'failed'
  const isSending = message._sendStatus === 'sending'

  const editing      = useSignal(false)
  const editText     = useSignal('')
  const editSaving   = useSignal(false)
  const editRef      = useRef(null)
  const bubbleRef    = useRef(null)

  // Consecutive: same sender within 5 min of previous message
  const isConsecutive = prevMessage &&
    prevMessage.creator_person?.id === creator.id &&
    !prevMessage._sendStatus &&
    !message._sendStatus &&
    (new Date(message.created_at) - new Date(prevMessage.created_at)) < CONSECUTIVE_THRESHOLD_MS

  const imageOnly = isImageOnlyMessage(message)
  const replyTo   = message.reply_to || null

  // Highlight flash: add CSS class then clear highlightMsgId after delay
  useEffect(() => {
    if (!highlighted) return
    const msgId = message.id ?? message._clientId
    const timer = setTimeout(() => {
      if (store.highlightMsgId.value === msgId) {
        store.highlightMsgId.value = null
      }
    }, 1600)
    return () => clearTimeout(timer)
  }, [highlighted]) // eslint-disable-line

  // Observe bubble height to switch action icons between row (single-line) and
  // column (multi-line) layout. Safe to use ResizeObserver here because the
  // actions are position:absolute — changing their flex-direction does not
  // affect the bubble's height and cannot trigger the MessageList scroll snap.
  // Hysteresis prevents oscillation: switch to column at >=58px, back to row
  // only when dropping below 50px. The dead-zone (50–57px) stops the loop that
  // would otherwise occur when the bubble sits right at the threshold.
  useEffect(() => {
    const bubble = bubbleRef.current
    if (!bubble) return
    let isColumn = false
    const update = () => {
      const h = bubble.offsetHeight
      if (!isColumn && h >= 58) {
        isColumn = true
        bubble.dataset.multiline = '1'
      } else if (isColumn && h < 50) {
        isColumn = false
        bubble.dataset.multiline = '0'
      }
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(bubble)
    return () => ro.disconnect()
  }, []) // eslint-disable-line

  function handleReplyClick(e) {
    e.preventDefault()
    const id = replyTo?.id
    if (!id) return
    const el = document.getElementById(`chat-message-${id}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    store.highlightMsgId.value = id
  }

  function handleReplyAction() {
    store.replyTo.value = message
  }

  async function handleRetry() {
    if (!message._retryData) return
    const { body, attachments, reply_to_id } = message._retryData
    // Remove failed message from pendingMsgs
    store.pendingMsgs.value = store.pendingMsgs.value.filter(
      m => m._clientId !== message._clientId
    )
    try {
      await chatApi.messages.create(roomId, { body, attachments, reply_to_id })
    } catch { /* ignore — new optimistic push will handle */ }
  }

  function handleCopy() {
    const text = message.body || ''
    const plain = text.replace(/@\[([^\]]+)\]\(\d+\)/g, '@$1')
    navigator.clipboard?.writeText(plain).catch(() => { /* ignore */ })
  }

  function handleEditStart() {
    editText.value = message.body || ''
    editing.value  = true
    requestAnimationFrame(() => {
      const el = editRef.current
      if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length) }
    })
  }

  async function handleEditSave() {
    const body = editText.value.trim()
    if (!body || body === message.body || editSaving.value) return
    editSaving.value = true
    try {
      await chatApi.messages.update(message.id, { body })
      editing.value = false
    } catch { /* leave editor open on failure */ }
    editSaving.value = false
  }

  function handleEditCancel() {
    editing.value = false
  }

  function handleEditKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEditSave() }
    if (e.key === 'Escape') handleEditCancel()
  }

  async function handleDelete() {
    if (!window.confirm(t('chat.messages.delete_confirm'))) return
    try {
      await chatApi.messages.destroy(message.id)
    } catch { /* ignore */ }
  }

  const msgId   = message.id ?? message._clientId
  const timeStr = message.created_at
    ? new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : ''

  const classes = [
    'chat-message',
    isOwn ? 'chat-message--own' : '',
    isConsecutive ? 'chat-message--consecutive' : '',
    highlighted ? 'chat-message--highlight' : '',
    isSending ? 'chat-message--sending' : '',
    isFailed ? 'chat-message--failed' : '',
  ].filter(Boolean).join(' ')

  const avatarUrl = creator.avatar_url || null
  const name      = creator.name || ''

  return (
    <div
      class={classes}
      id={message.id ? `chat-message-${message.id}` : undefined}
      data-message-id={message.id || undefined}
      data-own={isOwn ? 'true' : 'false'}
      data-client-message-id={message._clientId || undefined}
      style={isSending ? 'opacity:0.6' : undefined}
    >
      <div class="chat-message-main">
        {/* Avatar — hidden for own messages and consecutive bubbles */}
        {!isOwn && !isConsecutive && (
          <button
            type="button"
            class="chat-msg-avatar-wrap chat-msg-avatar-wrap--clickable"
            title={name}
            onClick={() => { store.profilePerson.value = { id: creator.id, name: creator.name, avatar_url: creator.avatar_url } }}
          >
            {avatarUrl
              ? <img src={avatarUrl} class="chat-msg-avatar" width={32} height={32} alt={name} title={name}
                     onLoad={e => e.target.classList.add('is-loaded')}
                     onError={e => { e.target.style.display = 'none' }}
                />
              : <div class="chat-msg-avatar chat-msg-avatar--initials" title={name}>{name.slice(0, 1).toUpperCase()}</div>
            }
          </button>
        )}
        {/* Spacer so consecutive bubbles align with non-consecutive ones */}
        {!isOwn && isConsecutive && <div class="chat-msg-avatar-spacer" />}

        <div class="chat-msg-body">
          {/* Sender name — only for first in group from others */}
          {!isOwn && !isConsecutive && (
            <div class="chat-message-sender">{name}</div>
          )}

          {/* Reply quote */}
          {replyTo && (
            <a
              class="chat-reply-quote"
              href={`#chat-message-${replyTo.id}`}
              onClick={handleReplyClick}
            >
              <span class="chat-reply-quote-author">
                {replyTo.creator_person?.name || replyTo.creator_name || ''}
              </span>
              <span
                class="chat-reply-quote-text"
                dangerouslySetInnerHTML={{
                  __html: renderMarkdownPreview(
                    (replyTo.body || replyTo.body_preview || '').slice(0, 120)
                  ) || ((replyTo.has_attachments || replyTo.attachments?.length) ? '[melléklet]' : '—'),
                }}
              />
            </a>
          )}

          <div class="chat-bubble-row">
            <div ref={bubbleRef} class={`chat-bubble ${isOwn ? 'chat-bubble--own' : 'chat-bubble--other'}${isSending ? ' chat-bubble--sending' : ''}${imageOnly ? ' chat-bubble--image-only' : ''}`}>
              {/* Attachments */}
              {(message.attachments || []).length > 0 && (
                <div class="chat-attachments mb-2">
                  {message.attachments.map((att, i) => (
                    <AttachmentView key={i} attachment={att} roomId={message.chat_room_id ?? roomId} messageId={message.id} />
                  ))}
                </div>
              )}

              {/* Message body — or inline edit form */}
              {editing.value
                ? (
                  <div class="chat-edit-form">
                    <textarea
                      ref={editRef}
                      class="chat-edit-textarea"
                      value={editText.value}
                      onInput={e => { editText.value = e.target.value }}
                      onKeyDown={handleEditKeyDown}
                      rows={3}
                    />
                    <div class="chat-edit-actions">
                      <button type="button" class="chat-edit-btn chat-edit-btn--save" onClick={handleEditSave} disabled={editSaving.value}>
                        {editSaving.value ? '…' : t('chat.rooms.save_changes')}
                      </button>
                      <button type="button" class="chat-edit-btn chat-edit-btn--cancel" onClick={handleEditCancel}>
                        {t('chat.rooms.cancel')}
                      </button>
                    </div>
                  </div>
                )
                : !imageOnly && <BubbleContent message={message} isImageOnly={false} />
              }

              {/* Failed state */}
              {isFailed && (
                <div class="chat-bubble-failed">
                  <span class="text-danger small">{t('chat.messages.send_failed')}</span>
                  <button
                    type="button"
                    class="btn btn-sm btn-link text-danger p-0 ms-2"
                    onClick={handleRetry}
                  >
                    {t('chat.messages.retry')}
                  </button>
                </div>
              )}

              {/* Hover actions — anchored to the bubble so positioning is always
                  relative to the bubble edge regardless of the row's width.
                  own messages: left side; other messages: right side (via CSS). */}
              {!isSending && !isFailed && (
                <div class="chat-msg-actions">
                  <button
                    type="button"
                    class="chat-msg-action-btn"
                    title={t('chat.messages.reply')}
                    onClick={handleReplyAction}
                  >
                    <i class="bi bi-reply-fill" />
                  </button>
                  {isOwn && message.id && (
                    <button
                      type="button"
                      class="chat-msg-action-btn"
                      title={t('chat.messages.edit')}
                      onClick={handleEditStart}
                    >
                      <i class="bi bi-pencil" />
                    </button>
                  )}
                  {isOwn && message.id && (
                    <button
                      type="button"
                      class="chat-msg-action-btn chat-msg-action-btn--danger"
                      title={t('chat.messages.delete')}
                      onClick={handleDelete}
                    >
                      <i class="bi bi-trash" />
                    </button>
                  )}
                  <BubbleReactions
                    message={message}
                    store={store}
                    roomId={roomId}
                    inline={false}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Reaction chips */}
          <BubbleReactions
            message={message}
            store={store}
            roomId={roomId}
            inline={true}
          />

          {/* Timestamp */}
          <div class="chat-message-time">
            {isSending && (
              <span class="chat-sending-dots">
                <span /><span /><span />
              </span>
            )}
            {!isSending && timeStr && (
              <time dateTime={message.created_at}>{timeStr}</time>
            )}
          </div>
        </div>
      </div>

      {/* Read receipts — visible on every bubble to show conversation progress */}
      {message.id && (
        <BubbleReadReceipts
          message={message}
          store={store}
          allMessages={allMessages}
          personId={personId}
        />
      )}
    </div>
  )
}
